"use client";

import { useEffect, useCallback } from "react";
import "./count-in.css";

export type CountInPhase = "idle" | "active" | "exiting";

export type CountInProps = {
  phase: CountInPhase;
  beat: number;
  totalBeats: number;
  beatDuration: number;
  /**
   * Marges (px) du couloir où le chiffre se centre. L'overlay entier capte le
   * clic, mais ce qu'on regarde se place dans ce qui reste une fois ces marges
   * retirées : le studio y met la minimap à droite, et en bas le clavier plus
   * la bande de passé, pour que le chiffre tombe entre le haut du roll et la
   * playhead. Zéro par défaut : centré sur tout le parent.
   */
  insetRight?: number;
  insetBottom?: number;
  onSkip?: () => void;
};

/**
 * Durée de décompte (ms) en dessous de laquelle le rappel « Espace ou clic »
 * ne s'affiche pas. À 120 à la noire, quatre temps durent deux secondes :
 * personne ne lit une consigne pour en gagner deux. Il ne sert qu'aux tempos
 * de travail, où l'attente est assez longue pour qu'on veuille la sauter.
 */
const HINT_MIN_COUNT_MS = 3000;

/**
 * Count-in overlay, pure presentational component.
 * Phase and beat state are controlled by the parent.
 *
 * Ni cadre ni voile : un chiffre, son anneau et les pastilles des temps, posés
 * sur le roll. Le morceau reste lisible autour, c'est lui qu'on s'apprête à
 * jouer.
 */
export function CountIn({
  phase,
  beat,
  totalBeats,
  beatDuration,
  insetRight = 0,
  insetBottom = 0,
  onSkip,
}: CountInProps) {
  const handleSkip = useCallback(() => {
    onSkip?.();
  }, [onSkip]);

  // Skip on Space/Enter
  useEffect(() => {
    if (phase !== "active") {
      return;
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [phase, handleSkip]);

  if (phase === "idle") {
    return null;
  }

  const isExiting = phase === "exiting";
  const isFinal = beat === totalBeats - 1;
  const intensity = totalBeats > 1 ? beat / (totalBeats - 1) : 1;
  const showHint = totalBeats * beatDuration >= HINT_MIN_COUNT_MS;
  const beatVars = {
    "--beat-duration": `${beatDuration}ms`,
    "--intensity": intensity,
  } as React.CSSProperties;

  return (
    <div
      className={`absolute inset-0 z-20 ${isExiting ? "pointer-events-none" : "pointer-events-auto"}`}
      role="status"
      aria-live="assertive"
      onClick={isExiting ? undefined : handleSkip}
    >
      <div
        className="absolute left-0 top-0 flex items-center justify-center pointer-events-none ci-lane"
        style={{ right: insetRight, bottom: insetBottom }}
      >
        <div
          className={`relative flex flex-col items-center gap-[0.22em] ci-block${isExiting ? " ci-block--exit" : ""}`}
          style={beatVars}
        >
          <div className="relative flex items-center justify-center">
            <span key={beat} className={`tabular-nums ci-number${isFinal ? " ci-number--final" : ""}`}>
              {beat + 1}
            </span>

            <span key={`ring-${beat}`} className={`ci-ring${isFinal ? " ci-ring--final" : ""}`} />
          </div>

          <div className="flex gap-2">
            {Array.from({ length: totalBeats }, (_, i) => (
              <span
                key={`pip-${i}`}
                className={`ci-pip${i <= beat ? " ci-pip--on" : ""}${i <= beat && isFinal ? " ci-pip--final" : ""}`}
              />
            ))}
          </div>

          {showHint && <span className="ci-hint">Espace ou clic pour continuer</span>}
        </div>
      </div>
    </div>
  );
}
